import { AlertCircle } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

export interface EditableQueryJSONProps {
  value: string;
  onChange: (value: string) => void;
  onExecute: (validJson: string) => void;
  editable?: boolean;
  valueRef?: React.MutableRefObject<string>;
  errors?: ValidationError[];
}

export interface ValidationError {
  line: number;
  message: string;
  type: 'syntax' | 'type' | 'semantic';
}

interface ErrorIconProps {
  error: ValidationError;
  lineHeight: number;
}

const ErrorIcon = ({ error, lineHeight }: ErrorIconProps) => {
  const topPosition = error.line * lineHeight;

  return (
    <div
      className="group pointer-events-auto absolute"
      style={{ top: `${topPosition + 8}px`, left: '8px' }}
      data-testid="error-icon"
    >
      <AlertCircle className="size-4 cursor-help text-red-500" />

      {/* CSS Tooltip */}
      <div
        className="border-chariot-stroke bg-chariot-background-primary absolute top-0 left-6 z-50 hidden w-64 rounded-md border p-2 shadow-lg group-hover:block"
        role="tooltip"
      >
        <p className="text-chariot-text-primary text-xs">{error.message}</p>
        <div className="border-chariot-stroke bg-chariot-background-primary absolute top-1 -left-1 size-2 rotate-45 border-b border-l"></div>
      </div>
    </div>
  );
};

/**
 * EditableQueryJSON - JSON editor with validation and error display
 *
 * Features:
 * - Editable textarea for JSON editing
 * - Execute button with validation
 * - Visual error indicators (gutter icons)
 * - Zod schema validation for GraphQuery types
 */
export const EditableQueryJSON = ({
  value,
  onChange,
  onExecute: _onExecute,
  editable = true,
  valueRef,
  errors = [],
}: EditableQueryJSONProps) => {
  const [editedJson, setEditedJson] = useState(value);
  const [lineHeight, setLineHeight] = useState(20);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync editedJson with value prop when it changes
  useEffect(() => {
    setEditedJson(value);
  }, [value]);

  // Sync editedJson to valueRef whenever it changes
  useEffect(() => {
    if (valueRef) {
      valueRef.current = editedJson;
    }
  }, [editedJson, valueRef]);

  // Measure line height on mount
  useEffect(() => {
    if (textareaRef.current) {
      const computed = window.getComputedStyle(textareaRef.current);
      const height = parseInt(computed.lineHeight);
      if (!isNaN(height)) {
        setLineHeight(height);
      }
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setEditedJson(newValue);
    onChange(newValue);
  };

  return (
    <div className="relative flex h-full flex-col">
      {/* Error Gutter - Absolute positioned overlay */}
      {errors.length > 0 && (
        <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-20 w-8">
          {errors.map((error, index) => (
            <ErrorIcon key={index} error={error} lineHeight={lineHeight} />
          ))}
        </div>
      )}

      {/* Textarea with left padding for error gutter */}
      <textarea
        ref={textareaRef}
        className="text-chariot-text-primary bg-chariot-background-secondary h-full w-full resize-none overflow-auto border-none py-6 pr-6 pl-10 font-mono text-sm whitespace-pre-wrap outline-none"
        style={{ lineHeight: `${lineHeight}px` }}
        value={editedJson}
        onChange={handleChange}
        readOnly={!editable}
        maxLength={51200} // 50KB character limit
        data-testid="query-editor-textarea"
        aria-label="JSON query editor"
        spellCheck={false}
      />
    </div>
  );
};
